// @ts-nocheck
import { MessageSquare, Volume2, Music, PhoneForwarded, Voicemail, PhoneOff, Upload, X } from 'lucide-react'
import { useState } from 'react'
import { useApi } from '../../hooks/useApi'

const TTS_VOICES = [
  { value: 'Polly.Mizuki', label: 'Mizuki（女性・日本語）' },
  { value: 'Polly.Takumi', label: 'Takumi（男性・日本語）' },
  { value: 'Polly.Kazuha', label: 'Kazuha（女性・日本語）' },
  { value: 'Polly.Tomoko', label: 'Tomoko（女性・日本語）' },
]

export default function ClosedMessageEditor({ closedMessage, closedAction, onChange }) {
  const api = useApi()
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleMessageChange = (updates) => {
    onChange({ closedMessage: { ...closedMessage, ...updates } })
  }

  const handleActionChange = (updates) => {
    onChange({ closedAction: { ...closedAction, ...updates } })
  }

  // 音声ファイルアップロード処理（共通）
  const uploadAudioFile = async (file) => {
    if (!file) return

    // ファイル形式チェック
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav']
    if (!allowedTypes.includes(file.type)) {
      setUploadError('MP3またはWAV形式のファイルを選択してください')
      return
    }

    // ファイルサイズチェック（10MB以下）
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('ファイルサイズは10MB以下にしてください')
      return
    }

    try {
      setUploading(true)
      setUploadError(null)

      // FormDataを作成
      const formData = new FormData()
      formData.append('audio', file)

      // S3にアップロード（uploadFileメソッドを使用）
      const data = await api.uploadFile('/api/upload/audio', formData)
      
      // アップロードされたファイルのURLを設定
      handleMessageChange({ audioUrl: data.url })
    } catch (err) {
      console.error('アップロードエラー:', err)
      setUploadError(err.message)
    } finally {
      setUploading(false)
    }
  }

  // ファイル選択時のハンドラー
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0]
    await uploadAudioFile(file)
  }

  // ドラッグ&ドロップハンドラー
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    await uploadAudioFile(file)
  }

  // 音声ファイル削除
  const handleRemoveAudio = () => {
    handleMessageChange({ audioUrl: '' })
    setUploadError(null)
  }

  return (
    <div className="space-y-6">
      {/* メッセージ設定 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className="text-md font-semibold text-gray-900 dark:text-white">
                営業時間外メッセージ
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                営業時間外に再生するメッセージを設定
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* メッセージタイプ選択 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              メッセージタイプ
            </label>
            <div className="flex gap-3">
              <label className="flex-1 flex items-center gap-3 p-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors has-[:checked]:border-orange-500 has-[:checked]:bg-orange-50 dark:has-[:checked]:bg-orange-900/20">
                <input
                  type="radio"
                  name="messageType"
                  value="tts"
                  checked={closedMessage.type === 'tts'}
                  onChange={(e) => handleMessageChange({ type: e.target.value })}
                  className="w-4 h-4 text-orange-600"
                />
                <Volume2 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  音声合成（TTS）
                </span>
              </label>
              <label className="flex-1 flex items-center gap-3 p-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors has-[:checked]:border-orange-500 has-[:checked]:bg-orange-50 dark:has-[:checked]:bg-orange-900/20">
                <input
                  type="radio"
                  name="messageType"
                  value="audio"
                  checked={closedMessage.type === 'audio'}
                  onChange={(e) => handleMessageChange({ type: e.target.value })}
                  className="w-4 h-4 text-orange-600"
                />
                <Music className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  音声ファイル
                </span>
              </label>
            </div>
          </div>

          {/* TTS設定 */}
          {closedMessage.type === 'tts' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  メッセージ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={closedMessage.ttsText}
                  onChange={(e) => handleMessageChange({ ttsText: e.target.value })}
                  rows={4}
                  placeholder="現在、営業時間外です。営業時間は平日9時から18時までです。"
                  className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  次回営業時間は自動的に案内されます
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  音声
                </label>
                <select
                  value={closedMessage.ttsVoice}
                  onChange={(e) => handleMessageChange({ ttsVoice: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  {TTS_VOICES.map((voice) => (
                    <option key={voice.value} value={voice.value}>
                      {voice.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* 音声ファイル設定 */}
          {closedMessage.type === 'audio' && (
            <div className="space-y-4">
              {/* ファイルアップロード */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  音声ファイル <span className="text-red-500">*</span>
                </label>
                
                {!closedMessage.audioUrl ? (
                  <div>
                    <label
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                        uploading
                          ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 cursor-not-allowed'
                          : isDragging
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 scale-105'
                          : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className={`w-8 h-8 mb-2 ${isDragging ? 'text-orange-500' : 'text-gray-500 dark:text-gray-400'}`} />
                        <p className={`mb-1 text-sm font-medium ${isDragging ? 'text-orange-700 dark:text-orange-300' : 'text-gray-700 dark:text-gray-300'}`}>
                          {isDragging ? 'ファイルをドロップ' : 'クリックまたはドラッグ&ドロップ'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          MP3またはWAV（最大10MB）
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav"
                        onChange={handleFileUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                    {uploading && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        アップロード中...
                      </div>
                    )}
                    {uploadError && (
                      <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {uploadError}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <Music className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-900 dark:text-green-100">
                        音声ファイルがアップロード済み
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300 truncate">
                        {closedMessage.audioUrl}
                      </p>
                    </div>
                    <button
                      onClick={handleRemoveAudio}
                      className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="削除"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* 手動URL入力（オプション） */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  または、URLを直接入力
                </label>
                <input
                  type="url"
                  value={closedMessage.audioUrl}
                  onChange={(e) => handleMessageChange({ audioUrl: e.target.value })}
                  placeholder="https://example.com/closed-message.mp3"
                  className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  既にホスティングされている音声ファイルのURLを入力できます
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* アクション設定 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
              <PhoneForwarded className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3 className="text-md font-semibold text-gray-900 dark:text-white">
                メッセージ後のアクション
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                メッセージ再生後の処理を選択
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* アクション選択 */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50 dark:has-[:checked]:bg-indigo-900/20">
              <input
                type="radio"
                name="action"
                value="hangup"
                checked={closedAction.action === 'hangup'}
                onChange={(e) => handleActionChange({ action: e.target.value })}
                className="mt-0.5 w-4 h-4 text-indigo-600"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <PhoneOff className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    電話を切る
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  メッセージを再生後、自動的に通話を終了します
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50 dark:has-[:checked]:bg-indigo-900/20">
              <input
                type="radio"
                name="action"
                value="voicemail"
                checked={closedAction.action === 'voicemail'}
                onChange={(e) => handleActionChange({ action: e.target.value })}
                className="mt-0.5 w-4 h-4 text-indigo-600"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Voicemail className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    ボイスメール録音
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  メッセージを録音します（録音は通話履歴から再生できます）
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50 dark:has-[:checked]:bg-indigo-900/20">
              <input
                type="radio"
                name="action"
                value="forward"
                checked={closedAction.action === 'forward'}
                onChange={(e) => handleActionChange({ action: e.target.value })}
                className="mt-0.5 w-4 h-4 text-indigo-600"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <PhoneForwarded className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    転送
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  緊急連絡先や別の電話番号に転送します
                </p>
              </div>
            </label>
          </div>

          {/* ボイスメール設定 */}
          {closedAction.action === 'voicemail' && (
            <div className="pl-7 pt-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                最大録音時間（秒）
              </label>
              <input
                type="number"
                min="10"
                max="600"
                value={closedAction.voicemailConfig?.maxRecordingLength || 180}
                onChange={(e) => handleActionChange({
                  voicemailConfig: {
                    ...closedAction.voicemailConfig,
                    maxRecordingLength: parseInt(e.target.value) || 180
                  }
                })}
                className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                10秒〜600秒の範囲で設定（デフォルト: 180秒）
              </p>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                録音されたボイスメールは通話履歴から再生できます
              </p>
            </div>
          )}

          {/* 転送設定 */}
          {closedAction.action === 'forward' && (
            <div className="pl-7 pt-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                転送先電話番号 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={closedAction.forwardNumber}
                onChange={(e) => handleActionChange({ forwardNumber: e.target.value })}
                placeholder="090-1234-5678"
                className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
